import React from 'react';

const UnassignedCashView = React.memo(({
  item,
  rowId,
  accountMapping,
  hideDollarValues,
  formatCurrency,
  symbolsForModelsAccounts // Add this prop to receive cached data
}) => {
  // Extract model name from item
  const modelName = item['Security Level Model Name'];
  const modelString = typeof modelName === 'object' && modelName?.value
    ? String(modelName.value)
    : String(modelName || '');

  // Get account numbers from the item
  const getAccountNumbers = () => {
    const accountNumbersField = item['Account Numbers'] || item.accountNumbers || '';
    let accountNumbers = [];
    
    if (typeof accountNumbersField === 'string') {
      accountNumbers = accountNumbersField
        .split(',')
        .map(num => num.trim())
        .filter(num => num !== '');
    } else if (Array.isArray(accountNumbersField)) {
      accountNumbers = accountNumbersField.map(num => String(num).trim()).filter(num => num !== '');
    }
    
    return accountNumbers;
  };

  const accountNumbers = getAccountNumbers();

  // Extract cash data from cached symbols data
  const getCashDataFromCache = () => {
    if (!symbolsForModelsAccounts || !accountNumbers.length) return null;

    const filteredData = {};
    accountNumbers.forEach(accountNumber => {
      if (symbolsForModelsAccounts[accountNumber] && symbolsForModelsAccounts[accountNumber][modelString]) {
        filteredData[accountNumber] = symbolsForModelsAccounts[accountNumber][modelString];
      }
    });

    return Object.keys(filteredData).length > 0 ? filteredData : null;
  };

  const cashData = getCashDataFromCache();

  // Calculate account summaries
  const calculateAccountSummaries = () => {
    if (!cashData) return [];

    return Object.entries(cashData).map(([accountNumber, symbols]) => {
      const accountName = Object.keys(accountMapping).find(name => 
        accountMapping[name] === accountNumber
      ) || `Account ${accountNumber}`;

      let totalValue = 0;
      let totalQuantity = 0;
      const symbolCount = symbols.length;

      symbols.forEach(symbol => {
        const quantity = parseFloat(symbol.Quantity || 0);
        const price = parseFloat(symbol.Price || 0);
        totalQuantity += quantity;
        totalValue += quantity * price;
      });

      return {
        accountNumber,
        accountName,
        symbolCount,
        totalQuantity,
        totalValue,
        symbols
      };
    });
  };

  const accountSummaries = calculateAccountSummaries();

  // Show loading state if no cached data is available yet
  if (!symbolsForModelsAccounts) {
    return (
      <div className="p-3 bg-light">
        <div className="d-flex align-items-center">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span>Loading cash holdings...</span>
        </div>
      </div>
    );
  }

  if (!cashData || accountSummaries.length === 0) {
    return (
      <div className="p-3 bg-light">
        <div className="alert alert-info mb-0">
          <i className="fas fa-info-circle me-2"></i>
          No cash holdings found for {modelString}
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 bg-light border-top">
      <div>
        {accountSummaries.map(summary => (
          <div key={summary.accountNumber} className="d-flex flex-wrap align-items-center py-2 border-bottom" style={{ rowGap: '0.25rem' }}>
            {/* Account name and number left, symbol+value pairs right, both wrapable */}
            <div className="d-flex align-items-center flex-shrink-1" style={{ minWidth: 0, flex: '1 1 220px' }}>
              <h6 className="mb-0 fw-bold text-black text-truncate" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {summary.accountName} <span className="fw-normal">({summary.accountNumber})</span>
              </h6>
            </div>
            <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0, flex: '1 1 220px' }}>
              <ul className="list-unstyled mb-0">
                {summary.symbols.map((symbol, index) => {
                  const value = parseFloat(symbol.Quantity || 0) * parseFloat(symbol.Price || 0);
                  return (
                    <li key={index} className="d-flex justify-content-end align-items-center flex-wrap" style={{ fontSize: '0.85rem' }}>
                      <span className="fw text-dark me-2">{symbol.Symbol}</span>
                      <span
                        className={value < 0 ? "text-danger text-end" : "text-success text-end"}
                        style={{ minWidth: '100px', display: 'inline-block', wordBreak: 'break-all' }}
                      >
                        {formatCurrency(value)}
                      </span>
                    </li>
                  );
                })}
                <li className="d-flex justify-content-end align-items-center flex-wrap" style={{ fontSize: '0.95rem' }}>
                  <span className="fw-bold text-dark me-2">Investable Total</span>
                  <span
                    className={summary.totalValue < 0 ? "fw-bold text-danger fs-5 text-end" : "fw-bold text-success fs-5 text-end"}
                    style={{ minWidth: '100px', display: 'inline-block', wordBreak: 'break-all' }}
                  >
                    {formatCurrency(summary.totalValue)}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

export default UnassignedCashView;
